import type {
  Call,
  CallAnalyticsSummary,
  CallDirection,
  CallInput,
  CallScorecardEntry,
  CallStatus,
  CoachJoinMode,
  DialSession,
  DialSessionTarget,
  DialSessionType,
} from "@sales-ops-amplify/shared";
import { apiDelete, apiGet, apiPatch, apiPost, apiPut } from "./client";

export function listCalls(params?: {
  status?: CallStatus;
  direction?: CallDirection;
  purposeId?: string;
  dispositionId?: string;
  leadId?: string;
  listId?: string;
  activeOnly?: boolean;
  minDurationSec?: number;
  maxDurationSec?: number;
  dateFrom?: string;
  dateTo?: string;
  q?: string;
  limit?: number;
}): Promise<{ calls: Call[] }> {
  return apiGet(
    "/api/calls",
    params as Record<string, string | number | boolean | undefined>,
  );
}

export function listUpcomingCalls(): Promise<{ calls: Call[] }> {
  return apiGet("/api/calls/upcoming");
}

export function listActiveCalls(): Promise<{ calls: Call[] }> {
  return apiGet("/api/calls/active");
}

export function getCallAnalytics(params?: {
  status?: CallStatus;
  direction?: CallDirection;
  purposeId?: string;
  dispositionId?: string;
  dateFrom?: string;
  dateTo?: string;
}): Promise<{ analytics: CallAnalyticsSummary }> {
  return apiGet(
    "/api/calls/analytics",
    params as Record<string, string | number | boolean | undefined>,
  );
}

export function getCall(callId: string): Promise<{ call: Call }> {
  return apiGet(`/api/calls/${callId}`);
}

export function getCallByShareToken(
  token: string,
): Promise<{ call: Call }> {
  return apiGet(`/api/calls/share/${encodeURIComponent(token)}`);
}

export function createCall(
  input: CallInput,
): Promise<{ call: Call }> {
  return apiPost("/api/calls", input);
}

export function prepareOutbound(input: {
  to: string;
  leadId?: string;
  leadName?: string;
  opportunityId?: string;
  accountId?: string;
  sequenceId?: string;
  enrollmentId?: string;
  listId?: string;
  phoneNumberId?: string;
  useLocalPresence?: boolean;
}): Promise<{ call: Call; fromNumber?: string; mode: "voip" | "bridge" }> {
  return apiPost("/api/calls/outbound", input);
}

export function updateCall(
  callId: string,
  fields: Partial<Call>,
): Promise<{ call: Call }> {
  return apiPatch(`/api/calls/${callId}`, fields);
}

export function completeCall(
  callId: string,
  body: {
    purposeId?: string;
    dispositionId?: string;
    notes?: string;
    durationSec?: number;
  },
): Promise<{ call: Call }> {
  return apiPost(`/api/calls/${callId}/complete`, body);
}

export function deleteCallRecording(
  callId: string,
): Promise<{ call: Call }> {
  return apiDelete(`/api/calls/${callId}/recording`);
}

export function getVoiceToken(): Promise<{
  token: string;
  identity: string;
  configured: boolean;
}> {
  return apiGet("/api/calls/token");
}

export function dropVoicemail(
  callId: string,
  voicemailDropId: string,
): Promise<{ twimlHint: string; redirected?: boolean }> {
  return apiPost(`/api/calls/${callId}/voicemail-drop`, { voicemailDropId });
}

export function joinActiveCall(
  callId: string,
  mode: CoachJoinMode,
): Promise<{ conferenceName: string; call: Call }> {
  return apiPost(`/api/calls/${callId}/join`, { mode });
}

export function regenerateInsights(
  callId: string,
): Promise<{ call: Call }> {
  return apiPost(`/api/calls/${callId}/insights`, {});
}

export function askAboutCall(
  callId: string,
  question: string,
): Promise<{ answer: string; call: Call }> {
  return apiPost(`/api/calls/${callId}/ask`, { question });
}

export function createCallShare(callId: string): Promise<{
  shareToken: string;
  sharePath: string;
  call: Call;
}> {
  return apiPost(`/api/calls/${callId}/share`, {});
}

export function markCallInterested(
  callId: string,
  interested = true,
): Promise<{ call: Call }> {
  return apiPost(`/api/calls/${callId}/interested`, { interested });
}

export function saveCallScorecard(
  callId: string,
  scorecard: CallScorecardEntry[],
): Promise<{ call: Call }> {
  return apiPut(`/api/calls/${callId}/scorecard`, { scorecard });
}

export function startDialSession(body: {
  type: DialSessionType;
  leadIds?: string[];
  targets?: DialSessionTarget[];
  concurrency?: number;
}): Promise<{ session: DialSession }> {
  return apiPost("/api/calls/dial-sessions", body);
}

export function getDialSession(
  sessionId: string,
): Promise<{ session: DialSession }> {
  return apiGet(`/api/calls/dial-sessions/${sessionId}`);
}

export function advanceDialSession(
  sessionId: string,
  completedCallId?: string,
): Promise<{ session: DialSession }> {
  return apiPost(`/api/calls/dial-sessions/${sessionId}/advance`, {
    completedCallId,
  });
}

export function cancelDialSession(
  sessionId: string,
): Promise<{ session: DialSession }> {
  return apiPost(`/api/calls/dial-sessions/${sessionId}/cancel`, {});
}
